import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, ChevronDown, ChevronRight } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { NAV_ITEMS, LOGO_DARK_BG } from "@/lib/nav-data";
import api from "@/lib/api";

export default function Header() {
  const [openMenu, setOpenMenu] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [menuItems, setMenuItems] = useState(null);
  const location = useLocation();

  useEffect(() => {
    api.get("/menu-items/tree").then(res => {
      if (res.data && res.data.length > 0) {
        // Filter visible items and map to nav format
        const filtered = res.data
          .filter(i => i.visible !== false)
          .map(buildNavItem);
        setMenuItems(filtered);
      }
    }).catch(() => {});
  }, []);

  const navItems = menuItems || NAV_ITEMS;

  return (
    <header data-testid="main-header" className="sticky top-0 z-50 bg-jci-black/95 backdrop-blur-md border-b border-white/10">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="flex items-center justify-between h-16 lg:h-20">
          <Link to="/" data-testid="logo-link" className="flex-shrink-0">
            <img src={LOGO_DARK_BG} alt="JCI Bangladesh" className="h-10 lg:h-12 w-auto" />
          </Link>

          <nav className="hidden lg:flex items-center gap-0.5" data-testid="desktop-nav">
            {navItems.map((item, i) => (
              <div key={i} className="relative" onMouseEnter={() => setOpenMenu(i)} onMouseLeave={() => setOpenMenu(null)}>
                {item.children && item.children.length > 0 ? (
                  <button
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    className="flex items-center gap-1 px-3 py-2 text-[13px] font-medium text-white/80 hover:text-white tracking-wide uppercase transition-colors"
                  >
                    {item.label}
                    <ChevronDown className="w-3 h-3" />
                  </button>
                ) : (
                  <Link
                    to={item.path || "#"}
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    className={`px-3 py-2 text-[13px] font-medium tracking-wide uppercase transition-colors nav-link-underline ${
                      location.pathname === item.path ? 'text-jci-blue' : 'text-white/80 hover:text-white'
                    }`}
                  >
                    {item.label}
                  </Link>
                )}

                {item.children && item.children.length > 0 && openMenu === i && (
                  <div className="absolute left-0 top-full pt-1 mega-menu-enter" data-testid={`dropdown-${item.label.toLowerCase().replace(/\s+/g, '-')}`}>
                    <div className="bg-white rounded-lg shadow-2xl border-t-[3px] border-jci-blue min-w-[260px] p-3">
                      {item.children.filter(c => c.visible !== false).map((child, j) => (
                        <div key={j}>
                          {child.children && child.children.length > 0 ? (
                            <NestedDropdown item={child} />
                          ) : (
                            <Link
                              to={child.path || "#"}
                              onClick={() => setOpenMenu(null)}
                              className="block px-3 py-2 text-sm text-jci-black hover:bg-jci-blue/5 hover:text-jci-blue rounded-md transition-colors"
                            >
                              {child.label}
                            </Link>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </nav>

          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button variant="ghost" size="icon" data-testid="mobile-menu-toggle" className="text-white">
                <Menu className="w-6 h-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] bg-jci-black border-l border-white/10 overflow-y-auto">
              <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
              <div className="pt-4">
                <img src={LOGO_DARK_BG} alt="JCI Bangladesh" className="h-8 mb-8" />
                <MobileNav items={navItems} onNavigate={() => setMobileOpen(false)} />
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}

function buildNavItem(item) {
  const result = { label: item.label, path: item.path || "" };
  if (item.children && item.children.length > 0) {
    result.children = item.children
      .filter(c => c.visible !== false)
      .map(buildNavItem);
  }
  return result;
}

function NestedDropdown({ item }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button className="flex items-center justify-between w-full px-3 py-2 text-sm text-jci-black hover:bg-jci-blue/5 hover:text-jci-blue rounded-md transition-colors">
        {item.label}
        <ChevronRight className="w-3 h-3" />
      </button>
      {open && (
        <div className="absolute left-full top-0 ml-1 bg-white rounded-lg shadow-xl border min-w-[240px] p-2 mega-menu-enter">
          {(item.children || []).filter(c => c.visible !== false).map((sub, k) => (
            <Link
              key={k}
              to={sub.path || "#"}
              className="block px-3 py-2 text-sm text-jci-black hover:bg-jci-blue/5 hover:text-jci-blue rounded-md transition-colors"
            >
              {sub.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function MobileNav({ items, onNavigate }) {
  const [expanded, setExpanded] = useState(null);
  const [subExpanded, setSubExpanded] = useState(null);

  return (
    <nav className="flex flex-col gap-1">
      {items.map((item, i) => (
        <div key={i}>
          {item.children && item.children.length > 0 ? (
            <>
              <button
                onClick={() => { setExpanded(expanded === i ? null : i); setSubExpanded(null); }}
                className="flex items-center justify-between w-full px-3 py-2.5 text-sm font-medium text-white/80 hover:text-white"
              >
                {item.label}
                <ChevronDown className={`w-4 h-4 transition-transform ${expanded === i ? 'rotate-180' : ''}`} />
              </button>
              {expanded === i && (
                <div className="ml-3 border-l border-white/10 pl-3">
                  {item.children.filter(c => c.visible !== false).map((child, j) => (
                    <div key={j}>
                      {child.children && child.children.length > 0 ? (
                        <>
                          <button
                            onClick={() => setSubExpanded(subExpanded === j ? null : j)}
                            className="flex items-center justify-between w-full px-2 py-2 text-sm text-white/60 hover:text-jci-blue"
                          >
                            {child.label}
                            <ChevronDown className={`w-3 h-3 transition-transform ${subExpanded === j ? 'rotate-180' : ''}`} />
                          </button>
                          {subExpanded === j && (
                            <div className="ml-3 border-l border-white/10 pl-3">
                              {child.children.filter(c => c.visible !== false).map((sub, k) => (
                                <Link key={k} to={sub.path || "#"} onClick={onNavigate} className="block px-2 py-1.5 text-xs text-white/50 hover:text-jci-blue">
                                  {sub.label}
                                </Link>
                              ))}
                            </div>
                          )}
                        </>
                      ) : (
                        <Link to={child.path || "#"} onClick={onNavigate} className="block px-2 py-2 text-sm text-white/60 hover:text-jci-blue">
                          {child.label}
                        </Link>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <Link to={item.path || "#"} onClick={onNavigate} className="block px-3 py-2.5 text-sm font-medium text-white/80 hover:text-white">
              {item.label}
            </Link>
          )}
        </div>
      ))}
    </nav>
  );
}
